import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard, CalendarRange, CalendarOff, FileText, Users, Car, Wrench,
  ClipboardCheck, Settings as SettingsIcon, LogOut, Languages,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const navItems = [
  { to: "/dashboard", key: "dashboard", Icon: LayoutDashboard },
  { to: "/planning", key: "planning", Icon: CalendarRange, roles: ["owner", "admin"] },
  { to: "/contracts", key: "contracts", Icon: FileText },
  { to: "/customers", key: "customers", Icon: Users, roles: ["owner", "admin"] },
  { to: "/vehicles", key: "vehicles", Icon: Car, roles: ["owner", "admin"] },
  { to: "/employees", key: "employees", Icon: Wrench, roles: ["owner", "admin"] },
  { to: "/absences", key: "absences", Icon: CalendarOff, roles: ["owner", "admin"] },
  { to: "/templates", key: "templates", Icon: ClipboardCheck, roles: ["owner", "admin"] },
  { to: "/settings", key: "settings", Icon: SettingsIcon, roles: ["owner", "admin"] },
];

function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const set = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem("autoflow_lang", lng);
  };
  const current = i18n.language?.slice(0, 2) || "de";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="text-zinc-300 hover:bg-[#2A2F3A] gap-2"
          data-testid="language-switcher"
        >
          <Languages className="h-4 w-4" />
          <span className="uppercase font-medium">{current}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="bg-[#21252E] border-[#2E3241] text-white">
        <DropdownMenuItem onClick={() => set("de")} data-testid="lang-de">Deutsch</DropdownMenuItem>
        <DropdownMenuItem onClick={() => set("en")} data-testid="lang-en">English</DropdownMenuItem>
        <DropdownMenuItem onClick={() => set("ro")} data-testid="lang-ro">Română</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Layout() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const visibleItems = navItems.filter((n) => !n.roles || n.roles.includes(user?.role));

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen app-bg text-zinc-100 flex">
      {/* Sidebar (desktop) */}
      <aside className="hidden md:flex w-64 shrink-0 flex-col surface border-r min-h-screen no-print">
        <div className="px-6 py-6 border-b border-[#2E3241] flex justify-center">
          <Logo size={70} />
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {visibleItems.map(({ to, key, Icon }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`nav-${key}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive
                    ? "bg-blue-500/15 text-blue-300 border border-blue-500/25"
                    : "text-zinc-400 hover:text-white hover:bg-[#2A2F3A] border border-transparent"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              <span>{t(`nav.${key}`)}</span>
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-[#2E3241] space-y-2">
          <div className="px-3 py-2 text-xs">
            <div className="text-zinc-400 truncate" data-testid="user-email">{user?.email}</div>
            <div className="uppercase tracking-wider text-zinc-500 mt-0.5">{user?.role}</div>
          </div>
          <div className="flex items-center justify-between gap-2 px-1">
            <LanguageSwitcher />
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-zinc-400 hover:text-white hover:bg-[#2A2F3A] gap-2"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden lg:inline">{t("nav.logout")}</span>
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile header */}
      <header className="md:hidden fixed top-0 inset-x-0 z-40 surface border-b flex items-center justify-between px-4 h-14 no-print">
        <Logo size={36} />
        <div className="flex items-center gap-1">
          <LanguageSwitcher />
          <Button
            variant="ghost"
            size="icon"
            onClick={handleLogout}
            className="text-zinc-400 hover:text-white"
            data-testid="logout-btn-mobile"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 min-w-0 md:pt-0 pt-14 pb-24 md:pb-0">
        <div className="p-4 md:p-6 lg:p-8 max-w-[1600px] mx-auto">
          <Outlet />
        </div>
      </main>

      {/* Bottom nav (mobile) — horizontally scrollable, iOS safe-area aware */}
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-40 surface border-t no-print"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        data-testid="mobile-bottom-nav"
      >
        <div
          className="flex items-center gap-1 px-2 h-16 overflow-x-auto overflow-y-hidden"
          style={{ WebkitOverflowScrolling: "touch", scrollbarWidth: "none" }}
        >
          {visibleItems.map(({ to, key, Icon }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`mob-nav-${key}`}
              className={({ isActive }) =>
                `shrink-0 flex flex-col items-center justify-center gap-1 min-w-[68px] px-2 py-1 rounded-md text-[10px] font-medium ${
                  isActive ? "text-blue-400 bg-blue-500/10" : "text-zinc-400"
                }`
              }
            >
              <Icon className="h-5 w-5" />
              <span className="truncate max-w-[64px]">{t(`nav.${key}`)}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
